/**
 * The strategic companion's key and envelope (M4 plan I2, D-099), shared by the save bridge
 * (`strategic-link/saveBridge.ts`, which writes them) and export/import (`exportImport.ts`, which bundles them).
 *
 * - The companion entry of an M2 record is keyed `${career.seed}:${career.gameTime}` (read from the record JSON).
 * - Each entry is an envelope `{ savedAt: epochMs, save }`. Only its shape is checked here; the strategic host
 *   validates the save itself (`deserializeState` with the save schema) when the Reach loads it.
 * - D-337 (save export carries the Reach): an exported file is the record JSON plus the top-level field
 *   `strategicCompanion: { version: 1, savedAt, save }`. Import strips it before the record is migrated and
 *   validated, then stores it under the imported record's key.
 */
import { z } from 'zod'
import type { StrategicSave } from '../strategic/types'

/** A stored strategic save: when it was written (epoch ms) and the save. */
export interface StrategicEnvelope {
  savedAt: number
  save: StrategicSave
}

const saveShape = z.custom<StrategicSave>((v) => typeof v === 'object' && v !== null && !Array.isArray(v), {
  message: 'Expected the strategic save object',
})

export const envelopeSchema = z.object({
  savedAt: z.number().finite(),
  save: saveShape,
})

/** The export field that carries the companion, and its format version. */
export const EXPORT_COMPANION_FIELD = 'strategicCompanion'
export const EXPORT_COMPANION_VERSION = 1

/** `strategicCompanion` in an exported file (version 1). */
export const exportCompanionSchema = z.strictObject({
  version: z.literal(EXPORT_COMPANION_VERSION),
  savedAt: z.number().finite(),
  save: saveShape,
})
export type ExportCompanion = z.infer<typeof exportCompanionSchema>

const recordKeySchema = z.object({
  savedAt: z.string(),
  career: z.object({ seed: z.number(), gameTime: z.number() }),
})

/**
 * The companion key of a record: the career's seed, its game time and the record's `savedAt`. M7 S41: the game clock
 * holds while docked, so several saves (a manual save, a fork's autosave, the next manual save) share seed and game
 * time; keyed by those alone, the newest save's Reach replaced the older slots' (a pre-fork slot then loaded the
 * post-fork worker). Without `savedAt` this is the legacy key (saves stored before S41), which loads fall back to.
 */
export function strategicKey(careerSeed: number, gameTime: number, savedAt?: string): string {
  return savedAt === undefined ? `${careerSeed}:${gameTime}` : `${careerSeed}:${gameTime}:${savedAt}`
}

/** The keys a load tries for a record, in order: its own, then the legacy key (saves stored before S41). */
export function companionKeys(careerSeed: number, gameTime: number, savedAt?: string): string[] {
  const legacy = strategicKey(careerSeed, gameTime)
  return savedAt === undefined ? [legacy] : [strategicKey(careerSeed, gameTime, savedAt), legacy]
}

/** An M2 record's companion key, its legacy key (before S41) and write time (epoch ms). */
export interface RecordKey {
  key: string
  legacyKey: string
  savedAtMs: number
}

/** The key and write time (epoch ms) of an M2 record (parsed JSON), or null when it is not a record. */
export function recordKeyOfValue(raw: unknown): RecordKey | null {
  const r = recordKeySchema.safeParse(raw)
  if (!r.success) return null
  const { seed, gameTime } = r.data.career
  const savedAtMs = Date.parse(r.data.savedAt)
  return { key: strategicKey(seed, gameTime, r.data.savedAt), legacyKey: strategicKey(seed, gameTime), savedAtMs: Number.isFinite(savedAtMs) ? savedAtMs : 0 }
}

/** The key and write time (epoch ms) of an M2 record text, or null when the text is not a record. */
export function recordKeyOf(text: string): RecordKey | null {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    return null
  }
  return recordKeyOfValue(raw)
}

export function encodeEnvelope(env: StrategicEnvelope): string {
  return JSON.stringify(env)
}
